/**
 * HeaderContainer - Container Component for Header
 *
 * Container component responsible for:
 * - Zustand store connections (global store, UI store, image library store)
 * - Auth state and logout logic
 * - Modal show functions (from UI store via useModal hooks)
 * - Encounter rolling business logic
 * - Active tab tracking side-effect
 *
 * This follows the Container/Presenter pattern as defined in ADR-034.
 * @see docs/adr/0034-container-presenter-component-pattern.md
 */

import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import {
  useCampaignLogModal,
  useDiceRollerModal,
  useEncounterModal,
  useScratchpadModal,
  useSessionModal,
  useSoundscapeModal,
  useWatchOrderModal,
} from './hooks/useModal';
import { useStore, useActions } from './hooks/useStore';
import { useImageLibraryStore } from './state/createImageLibraryStore';
import { useStableCallback } from './hooks/hookUtils';
import type { Monster, NPC, Image } from './types/types';
import Header from './Header';

/** Main navigation tab paths used for persisting the active tab to settings */
const MAIN_TABS = ['/', '/data', '/world-building', '/admin', '/settings'];

const HeaderContainer: React.FC = () => {
  // ---------------------------------------------------------------------------
  // Store Connections
  // ---------------------------------------------------------------------------
  const { user, isAuthenticated, logout } = useAuth();
  const soundscapes = useStore(state => state.session?.cache?.soundscapes ?? []);
  const encounters = useStore(state => state.session?.roster?.encounters ?? []);
  const activeMapId = useStore(state => state.session?.map?.activeMapId ?? null);
  const monsterRoster = useStore(state => state.session?.cache?.monsterRoster ?? []);
  const npcs = useStore(state => state.session?.roster?.npcs ?? []);
  const images = useImageLibraryStore(state => state.images);
  const actions = useActions();

  // ---------------------------------------------------------------------------
  // Modal Hooks (from UI store -- Zustand, not React context)
  // ---------------------------------------------------------------------------
  const { show: showLogModal } = useCampaignLogModal();
  const { show: showScratchpadModal } = useScratchpadModal();
  const { show: showSessionModal } = useSessionModal();
  const { show: showSoundscapeModal } = useSoundscapeModal();
  const { show: showDiceRollerModal } = useDiceRollerModal();
  const { show: showWatchOrderModal } = useWatchOrderModal();
  const { show: showEncounterModal } = useEncounterModal();

  // ---------------------------------------------------------------------------
  // Router
  // ---------------------------------------------------------------------------
  const location = useLocation();

  // ---------------------------------------------------------------------------
  // Side Effects
  // ---------------------------------------------------------------------------

  // Persist the active main tab to settings when the user navigates
  useEffect(() => {
    const currentMainTab = (MAIN_TABS ?? []).find(
      tab => location.pathname.startsWith(tab) && (tab !== '/' || location.pathname === '/')
    );
    if (currentMainTab) {
      actions.updateSettings({ activeMainTab: currentMainTab });
    }
  }, [location.pathname, actions]);

  // ---------------------------------------------------------------------------
  // Encounter Rolling Business Logic
  // ---------------------------------------------------------------------------

  /**
   * Roll a random encounter for the currently active map.
   *
   * Logic:
   * 1. Filter all encounters to those belonging to the active map.
   * 2. Pick one encounter at random.
   * 3. Roll each creature's `chance` (0-100) -- only creatures that pass appear.
   * 4. Pick the first creature that passed, resolve its name and image, show the modal.
   *
   * If no encounters exist for this map, or all chance rolls fail, the modal
   * opens with a null result ("The path is clear... for now.").
   */
  const handleRollEncounter = useStableCallback(() => {
    // Build a lookup map for image URLs (imageId -> URL)
    const imageMap = new Map<number, string>(
      (images ?? []).map((img: Image) => [img.id, img.imageUrl])
    );

    // Only consider encounters tied to the currently displayed map
    const mapEncounters = (encounters ?? []).filter(e => e.mapId === activeMapId);

    if (mapEncounters.length === 0) {
      // No encounter tables defined for this map
      showEncounterModal(null);
      return;
    }

    // Pick a random encounter table from this map.
    // The length guard above ensures this index is valid.
    const encounter = mapEncounters[Math.floor(Math.random() * mapEncounters.length)];
    if (!encounter) {
      showEncounterModal(null);
      return;
    }

    // Roll each creature against its chance percentage (0-100).
    // A creature with chance=100 always appears; chance=0 never appears.
    const rolledCreatures = (encounter.creatures ?? []).filter(
      c => Math.random() * 100 < c.chance
    );

    if (rolledCreatures.length === 0) {
      // All creatures failed their chance rolls -- no encounter this time
      showEncounterModal(null);
      return;
    }

    // Pick one creature at random from those that passed their rolls.
    // The length guard above ensures this index is valid.
    const creature = rolledCreatures[Math.floor(Math.random() * rolledCreatures.length)];
    if (!creature) {
      showEncounterModal(null);
      return;
    }

    // Resolve the creature's display name and image based on its type
    let creatureName = 'Unknown Creature';
    let imageId: number | null = null;

    if (creature.creatureType === 'monster') {
      const monster = (monsterRoster ?? []).find((m: Monster) => m.id === creature.creatureId);
      if (monster) {
        creatureName = monster.name;
        imageId = monster.imageId;
      }
    } else if (creature.creatureType === 'npc') {
      const npc = (npcs ?? []).find((n: NPC) => n.id === creature.creatureId);
      if (npc) {
        creatureName = npc.name;
        imageId = npc.imageId ?? null;
      }
    }

    // quantity is stored as a string ("1", "2d6", etc.) -- parse to an integer for display.
    // For dice expressions we use the numeric prefix (e.g. "2d6" -> 2) as a simple
    // approximation; a proper dice roller is available via the Dice button.
    const quantity = parseInt(creature.quantity, 10) || 1;

    showEncounterModal({
      name: creatureName,
      quantity,
      image: imageId ? (imageMap.get(imageId) ?? null) : null,
    });
  });

  // ---------------------------------------------------------------------------
  // Build Presenter Props
  // ---------------------------------------------------------------------------

  const modalHandlers = {
    showLogModal,
    showScratchpadModal,
    showSessionModal,
    showSoundscapeModal,
    showDiceRollerModal,
    showWatchOrderModal,
  };

  return (
    <Header
      user={user}
      isAuthenticated={isAuthenticated}
      soundscapesAvailable={soundscapes.length > 0}
      modalHandlers={modalHandlers}
      onRollEncounter={handleRollEncounter}
      onLogout={logout}
    />
  );
};

export default HeaderContainer;
